import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const LEAFLET_CSS_ID = "leaflet-css";
const LEAFLET_SCRIPT_ID = "leaflet-script";
const LEAFLET_CSS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_SCRIPT_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const DEFAULT_CENTER = [13.0827, 80.2707];

const loadLeaflet = () =>
  new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    if (!document.getElementById(LEAFLET_CSS_ID)) {
      const leafletCss = document.createElement("link");
      leafletCss.id = LEAFLET_CSS_ID;
      leafletCss.rel = "stylesheet";
      leafletCss.href = LEAFLET_CSS_URL;
      document.head.appendChild(leafletCss);
    }

    const existingScript = document.getElementById(LEAFLET_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.L), { once: true });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const leafletScript = document.createElement("script");
    leafletScript.id = LEAFLET_SCRIPT_ID;
    leafletScript.src = LEAFLET_SCRIPT_URL;
    leafletScript.async = true;
    leafletScript.addEventListener("load", () => resolve(window.L), { once: true });
    leafletScript.addEventListener("error", reject, { once: true });
    document.body.appendChild(leafletScript);
  });

const createAttendanceIcon = (L, color) =>
  L.divIcon({
    className: "attendance-map-marker",
    html: `<span class="attendance-map-marker-pin attendance-map-marker-pin-${color}"></span>`,
    iconSize: [24, 34],
    iconAnchor: [12, 34],
    popupAnchor: [0, -32],
  });

const escapePopupValue = (value) =>
  String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getValidPosition = (latLong) => {
  if (!latLong || typeof latLong !== "string") {
    return null;
  }

  const [latValue, lngValue] = latLong.split(",");
  const lat = parseFloat(latValue);
  const lng = parseFloat(lngValue);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  return [lat, lng];
};

const Map = forwardRef(({ zoom = 15 }, ref) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerLayerRef = useRef(null);
  const [emptyMessage, setEmptyMessage] = useState("");

  useImperativeHandle(ref, () => ({
    async googleMap(data = []) {
      const L = await loadLeaflet();

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(mapInstanceRef.current);
      }

      if (markerLayerRef.current) {
        markerLayerRef.current.clearLayers();
      } else {
        markerLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      }

      const bounds = [];

      data.forEach((item) => {
        const position = getValidPosition(item.start_time_latlong);

        if (!position) {
          return;
        }

        const isWorking = !item.project_end_time || item.project_end_time === "null";
        const markerColor = isWorking ? "green" : "red";

        L.marker(position, {
          icon: createAttendanceIcon(L, markerColor),
        })
          .bindPopup(`
            <div style="font-size: 14px; line-height: 1.6;">
              <strong>Project Name:</strong> ${escapePopupValue(item.project_name)}<br/>
              <strong>Start Time:</strong> ${escapePopupValue(item.project_start_time)}<br/>
              <strong>End Time:</strong> ${escapePopupValue(item.project_end_time || "Currently Working")}<br/>
              <strong>Total Hours:</strong> ${escapePopupValue(item.hours_of_working)}
            </div>
          `)
          .addTo(markerLayerRef.current);

        bounds.push(position);
      });

      if (bounds.length) {
        setEmptyMessage("");
        mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: zoom });
      } else {
        setEmptyMessage("No location data available for the selected employee/date.");
        mapInstanceRef.current.setView(DEFAULT_CENTER, zoom);
      }

      setTimeout(() => mapInstanceRef.current.invalidateSize(), 0);
    },
  }));

  return (
    <div className="attendance-map-wrapper">
      <div className="attendance-map" ref={mapRef} />
      {emptyMessage ? (
        <div className="attendance-map-empty">{emptyMessage}</div>
      ) : null}
    </div>
  );
});

export default Map;
